/**
 * BoardMeetingEditModal — full draft editor for one board meeting: logistics, attendance/quorum,
 * agenda, and the minutes (discussion + motions + resolution) recorded against each agenda item.
 * Only reachable while a meeting is still a draft — api/board-meetings.js's `update` action
 * rejects writes to a finalized meeting server-side regardless of what this modal sends.
 */

import { useState } from 'react'
import { updateBoardMeeting } from '../services/boardMeetings.js'

const MOTION_RESULTS = ['Approved unanimously', 'Approved', 'Rejected', 'Rejected unanimously', 'Tabled']

function toLocalInput(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const p = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
}
function fromLocalInput(str) { return str ? new Date(str).toISOString() : null }
let uidCounter = 0
const newId = () => 'a' + (++uidCounter) + '_' + Math.random().toString(36).slice(2, 7)

export default function BoardMeetingEditModal({ meeting, onClose, onSaved }) {
  const [meetingType, setMeetingType] = useState(meeting.meeting_type)
  const [title, setTitle] = useState(meeting.title || '')
  const [scheduledAt, setScheduledAt] = useState(toLocalInput(meeting.scheduled_at))
  const [location, setLocation] = useState(meeting.location || '')
  const [calledBy, setCalledBy] = useState(meeting.called_by || '')
  const [noticeGiven, setNoticeGiven] = useState(meeting.notice_given !== false)
  const [noticeWaived, setNoticeWaived] = useState(!!meeting.notice_waived)
  const [presidingOfficer, setPresidingOfficer] = useState(meeting.presiding_officer_name || '')
  const [actingSecretary, setActingSecretary] = useState(meeting.acting_secretary_name || '')
  const [callToOrderAt, setCallToOrderAt] = useState(toLocalInput(meeting.call_to_order_at))
  const [adjournedAt, setAdjournedAt] = useState(toLocalInput(meeting.adjourned_at))
  const [quorumMet, setQuorumMet] = useState(meeting.quorum_met === true ? 'true' : meeting.quorum_met === false ? 'false' : '')
  const [attendees, setAttendees] = useState(meeting.attendees || [])
  const [agenda, setAgenda] = useState((meeting.agenda || []).map(a => ({ ...a })))
  const [minutesMap, setMinutesMap] = useState(() => {
    const map = {}
    for (const m of (meeting.minutes || [])) map[m.agenda_item_id] = { discussion: m.discussion || '', motions: m.motions || [], resolution_text: m.resolution_text || '' }
    return map
  })
  const [otherBusiness, setOtherBusiness] = useState(meeting.other_business || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  function minutesFor(agendaId) { return minutesMap[agendaId] || { discussion: '', motions: [], resolution_text: '' } }
  function setMinutesFor(agendaId, patch) { setMinutesMap(prev => ({ ...prev, [agendaId]: { ...minutesFor(agendaId), ...patch } })) }

  function addAttendee() { setAttendees(a => [...a, { name: '', title: '', is_director: false, present: true }]) }
  function updateAttendee(i, patch) { setAttendees(a => a.map((x, j) => j === i ? { ...x, ...patch } : x)) }
  function removeAttendee(i) { setAttendees(a => a.filter((_, j) => j !== i)) }

  function addAgendaItem() { setAgenda(a => [...a, { id: newId(), title: '', presenter: '', notes: '' }]) }
  function updateAgendaItem(i, patch) { setAgenda(a => a.map((x, j) => j === i ? { ...x, ...patch } : x)) }
  function removeAgendaItem(i) { setAgenda(a => a.filter((_, j) => j !== i)) }
  function moveAgendaItem(i, dir) {
    setAgenda(a => {
      const j = i + dir
      if (j < 0 || j >= a.length) return a
      const next = a.slice()
      ;[next[i], next[j]] = [next[j], next[i]]
      return next
    })
  }

  async function save() {
    if (!title.trim() || !scheduledAt) { setError('Title and date/time are required.'); return }
    setSaving(true); setError('')
    try {
      const minutes = agenda.map(item => ({ agenda_item_id: item.id, ...minutesFor(item.id) }))
      const updated = await updateBoardMeeting(meeting.id, {
        meeting_type: meetingType, title: title.trim(), scheduled_at: fromLocalInput(scheduledAt),
        location: location.trim() || null, called_by: calledBy.trim() || null,
        notice_given: noticeGiven, notice_waived: noticeWaived,
        presiding_officer_name: presidingOfficer.trim() || null, acting_secretary_name: actingSecretary.trim() || null,
        call_to_order_at: fromLocalInput(callToOrderAt), adjourned_at: fromLocalInput(adjournedAt),
        quorum_met: quorumMet === 'true' ? true : quorumMet === 'false' ? false : null,
        attendees, agenda, minutes, other_business: otherBusiness.trim() || null,
      })
      onSaved(updated)
    } catch (e) {
      setError(e.message || 'Save failed.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={M.overlay} onClick={onClose}>
      <div style={M.card} onClick={e => e.stopPropagation()}>
        <div style={M.head}>
          <span style={M.title}>Edit Meeting</span>
          <button style={M.x} onClick={onClose}>&times;</button>
        </div>
        <div style={M.body}>
          {error && <div style={M.notice}>{error}</div>}

          <div style={M.row}>
            <Field label="Meeting type" style={{ flex: 1 }}>
              <select style={M.input} value={meetingType} onChange={e => setMeetingType(e.target.value)}>
                <option value="quarterly">Quarterly</option>
                <option value="special">Special</option>
                <option value="annual">Annual</option>
              </select>
            </Field>
            <Field label="Title" style={{ flex: 2 }}>
              <input style={M.input} value={title} onChange={e => setTitle(e.target.value)} />
            </Field>
          </div>
          <div style={M.row}>
            <Field label="Date & time" style={{ flex: 1 }}>
              <input type="datetime-local" style={M.input} value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} />
            </Field>
            <Field label="Location" style={{ flex: 1 }}>
              <input style={M.input} value={location} onChange={e => setLocation(e.target.value)} />
            </Field>
          </div>
          <div style={M.row}>
            <Field label="Called by" style={{ flex: 1 }}>
              <input style={M.input} value={calledBy} onChange={e => setCalledBy(e.target.value)} />
            </Field>
            <Field label="Notice" style={{ flex: 1 }}>
              <label style={M.checkboxLabel}><input type="checkbox" checked={noticeGiven} onChange={e => setNoticeGiven(e.target.checked)} /> Given as required</label>
              <label style={M.checkboxLabel}><input type="checkbox" checked={noticeWaived} onChange={e => setNoticeWaived(e.target.checked)} /> Waived by all directors</label>
            </Field>
          </div>

          <div style={M.sectionLabel}>Attendance & Quorum</div>
          <div style={M.row}>
            <Field label="Presiding officer" style={{ flex: 1 }}>
              <input style={M.input} value={presidingOfficer} onChange={e => setPresidingOfficer(e.target.value)} />
            </Field>
            <Field label="Acting secretary" style={{ flex: 1 }}>
              <input style={M.input} value={actingSecretary} onChange={e => setActingSecretary(e.target.value)} />
            </Field>
          </div>
          <div style={M.row}>
            <Field label="Called to order" style={{ flex: 1 }}>
              <input type="datetime-local" style={M.input} value={callToOrderAt} onChange={e => setCallToOrderAt(e.target.value)} />
            </Field>
            <Field label="Adjourned" style={{ flex: 1 }}>
              <input type="datetime-local" style={M.input} value={adjournedAt} onChange={e => setAdjournedAt(e.target.value)} />
            </Field>
            <Field label="Quorum" style={{ flex: 1 }}>
              <select style={M.input} value={quorumMet} onChange={e => setQuorumMet(e.target.value)}>
                <option value="">Not confirmed</option>
                <option value="true">Present</option>
                <option value="false">Not present</option>
              </select>
            </Field>
          </div>

          <Field label="Attendees">
            {attendees.map((a, i) => (
              <div key={i} style={M.subRow}>
                <div style={M.row}>
                  <input style={{ ...M.input, flex: 2 }} value={a.name} onChange={e => updateAttendee(i, { name: e.target.value })} placeholder="Name" />
                  <input style={{ ...M.input, flex: 1 }} value={a.title || ''} onChange={e => updateAttendee(i, { title: e.target.value })} placeholder="Title" />
                </div>
                <div style={M.row}>
                  <label style={M.checkboxLabel}><input type="checkbox" checked={!!a.is_director} onChange={e => updateAttendee(i, { is_director: e.target.checked })} /> Director</label>
                  <label style={M.checkboxLabel}><input type="checkbox" checked={a.present !== false} onChange={e => updateAttendee(i, { present: e.target.checked })} /> Present</label>
                  <button style={M.removeBtn} onClick={() => removeAttendee(i)}>Remove</button>
                </div>
              </div>
            ))}
            <button style={M.addBtn} onClick={addAttendee}>+ Add attendee</button>
          </Field>

          <Field label="Agenda & Minutes">
            {agenda.map((item, i) => (
              <div key={item.id} style={M.agendaBlock}>
                <div style={M.row}>
                  <input style={{ ...M.input, flex: 2 }} value={item.title} onChange={e => updateAgendaItem(i, { title: e.target.value })} placeholder="Agenda item title" />
                  <input style={{ ...M.input, flex: 1 }} value={item.presenter || ''} onChange={e => updateAgendaItem(i, { presenter: e.target.value })} placeholder="Presenter" />
                </div>
                <textarea style={{ ...M.input, minHeight: 60, marginTop: 6 }} value={minutesFor(item.id).discussion} onChange={e => setMinutesFor(item.id, { discussion: e.target.value })} placeholder="Discussion notes for this item" />
                <MotionsEditor value={minutesFor(item.id).motions} onChange={motions => setMinutesFor(item.id, { motions })} />
                <textarea style={{ ...M.input, minHeight: 40, marginTop: 6 }} value={minutesFor(item.id).resolution_text} onChange={e => setMinutesFor(item.id, { resolution_text: e.target.value })} placeholder="Resolution text (if this item resulted in a formal resolution)" />
                <div style={M.row}>
                  <button style={M.removeBtn} onClick={() => removeAgendaItem(i)}>Remove item</button>
                  <button style={M.smallBtn} onClick={() => moveAgendaItem(i, -1)} disabled={i === 0}>↑</button>
                  <button style={M.smallBtn} onClick={() => moveAgendaItem(i, 1)} disabled={i === agenda.length - 1}>↓</button>
                </div>
              </div>
            ))}
            <button style={M.addBtn} onClick={addAgendaItem}>+ Add agenda item</button>
          </Field>

          <Field label="Other business">
            <textarea style={{ ...M.input, minHeight: 60 }} value={otherBusiness} onChange={e => setOtherBusiness(e.target.value)} />
          </Field>
        </div>
        <div style={M.foot}>
          <button style={M.ghost} onClick={onClose}>Cancel</button>
          <button style={M.primary} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  )
}

function MotionsEditor({ value, onChange }) {
  function update(i, patch) { onChange(value.map((m, j) => j === i ? { ...m, ...patch } : m)) }
  function remove(i) { onChange(value.filter((_, j) => j !== i)) }
  function add() { onChange([...value, { text: '', moved_by: '', seconded_by: '', result: 'Approved unanimously', votes_for: null, votes_against: null, votes_abstain: null }]) }

  return (
    <div style={{ marginTop: 6 }}>
      {value.map((mo, i) => (
        <div key={i} style={M.motionRow}>
          <textarea style={{ ...M.input, minHeight: 36 }} value={mo.text} onChange={e => update(i, { text: e.target.value })} placeholder="Motion text" />
          <div style={M.row}>
            <input style={{ ...M.input, flex: 1 }} value={mo.moved_by || ''} onChange={e => update(i, { moved_by: e.target.value })} placeholder="Moved by" />
            <input style={{ ...M.input, flex: 1 }} value={mo.seconded_by || ''} onChange={e => update(i, { seconded_by: e.target.value })} placeholder="Seconded by" />
            <select style={{ ...M.input, flex: 1 }} value={mo.result || 'Approved unanimously'} onChange={e => update(i, { result: e.target.value })}>
              {MOTION_RESULTS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          {!mo.result?.includes('unanimously') && (
            <div style={M.row}>
              <input type="number" style={{ ...M.input, flex: 1 }} value={mo.votes_for ?? ''} onChange={e => update(i, { votes_for: e.target.value === '' ? null : Number(e.target.value) })} placeholder="Votes for" />
              <input type="number" style={{ ...M.input, flex: 1 }} value={mo.votes_against ?? ''} onChange={e => update(i, { votes_against: e.target.value === '' ? null : Number(e.target.value) })} placeholder="Votes against" />
              <input type="number" style={{ ...M.input, flex: 1 }} value={mo.votes_abstain ?? ''} onChange={e => update(i, { votes_abstain: e.target.value === '' ? null : Number(e.target.value) })} placeholder="Abstain" />
            </div>
          )}
          <button style={M.removeBtn} onClick={() => remove(i)}>Remove motion</button>
        </div>
      ))}
      <button style={M.addBtn} onClick={add}>+ Add motion</button>
    </div>
  )
}

function Field({ label, children, style }) {
  return (
    <div style={{ ...M.field, ...style }}>
      <div style={M.fieldLabel}>{label}</div>
      {children}
    </div>
  )
}

const M = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 700, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 16px', overflowY: 'auto' },
  card: { width: '100%', maxWidth: 680, display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-elevated)', borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,0.3)', maxHeight: '90vh' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '0.5px solid var(--aj-border-mid)', flexShrink: 0 },
  title: { fontFamily: 'Montserrat,sans-serif', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 13, fontWeight: 700, color: 'var(--aj-text)' },
  x: { background: 'none', border: 'none', fontSize: 22, lineHeight: 1, color: 'var(--aj-text-muted)', cursor: 'pointer' },
  body: { padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto' },
  foot: { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 18px', borderTop: '0.5px solid var(--aj-border-mid)', flexShrink: 0 },
  field: { display: 'flex', flexDirection: 'column' },
  fieldLabel: { fontFamily: '"DM Mono",monospace', fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 4 },
  sectionLabel: { fontFamily: 'Montserrat,sans-serif', fontWeight: 600, fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text)', borderTop: '0.5px solid var(--aj-border)', paddingTop: 12 },
  input: { width: '100%', boxSizing: 'border-box', padding: '7px 9px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', background: 'var(--aj-bg-elevated)' },
  row: { display: 'flex', gap: 10, alignItems: 'center' },
  subRow: { border: '0.5px solid var(--aj-border)', borderRadius: 6, padding: 8, marginBottom: 6, display: 'flex', flexDirection: 'column', gap: 6 },
  agendaBlock: { border: '0.5px solid var(--aj-border-mid)', borderRadius: 8, padding: 10, marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 6 },
  motionRow: { border: '0.5px solid var(--aj-border)', borderRadius: 6, padding: 8, marginBottom: 6, display: 'flex', flexDirection: 'column', gap: 6 },
  checkboxLabel: { display: 'flex', alignItems: 'center', gap: 5, fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)' },
  primary: { background: '#07272D', border: 'none', borderRadius: 4, padding: '7px 16px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: '#fff' },
  ghost: { background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '7px 16px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' },
  addBtn: { alignSelf: 'flex-start', background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '5px 10px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-mid)' },
  removeBtn: { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: '#c0392b' },
  smallBtn: { background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-mid)' },
  notice: { background: 'rgba(184,134,11,0.1)', border: '0.5px solid rgba(184,134,11,0.3)', borderRadius: 6, padding: '8px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#8a6508' },
}
